/* ============================================================
 * Favee Hero Slider
 * Lightweight fade-based carousel with autoplay, dots, swipe.
 * ============================================================ */

(function () {
  'use strict';

  const SELECTOR = '[data-favee-slider]';
  const SLIDE_SELECTOR = '[data-favee-slide]';
  const DOT_SELECTOR = '[data-favee-slider-dot]';
  const ACTIVE_CLASS = 'is-active';
  const SWIPE_THRESHOLD = 40;

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initSlider(root) {
    if (!root || root.dataset.faveeSliderReady === 'true') return;
    root.dataset.faveeSliderReady = 'true';

    const slides = Array.from(root.querySelectorAll(SLIDE_SELECTOR));
    const dots = Array.from(root.querySelectorAll(DOT_SELECTOR));
    if (slides.length <= 1) return;

    let current = 0;
    let timer = null;
    const intervalMs = parseInt(root.dataset.interval, 10) || 5000;
    const autoplay = root.dataset.autoplay !== 'false' && !prefersReducedMotion;

    function setActive(index) {
      const next = ((index % slides.length) + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle(ACTIVE_CLASS, i === next);
      });
      dots.forEach((dot, i) => {
        const isActive = i === next;
        dot.classList.toggle(ACTIVE_CLASS, isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      current = next;
    }

    function startTimer() {
      if (!autoplay) return;
      stopTimer();
      timer = window.setInterval(() => setActive(current + 1), intervalMs);
    }

    function stopTimer() {
      if (timer != null) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.index, 10);
        if (Number.isFinite(idx)) {
          setActive(idx);
          startTimer();
        }
      });
    });

    root.addEventListener('mouseenter', stopTimer);
    root.addEventListener('mouseleave', startTimer);
    root.addEventListener('focusin', stopTimer);
    root.addEventListener('focusout', startTimer);

    // Touch swipe
    let touchStartX = null;
    root.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches && e.touches.length === 1) {
          touchStartX = e.touches[0].clientX;
          stopTimer();
        }
      },
      { passive: true }
    );
    root.addEventListener(
      'touchend',
      (e) => {
        if (touchStartX == null) return;
        const endX = (e.changedTouches && e.changedTouches[0] && e.changedTouches[0].clientX) || touchStartX;
        const diff = endX - touchStartX;
        if (Math.abs(diff) > SWIPE_THRESHOLD) {
          setActive(diff < 0 ? current + 1 : current - 1);
        }
        touchStartX = null;
        startTimer();
      },
      { passive: true }
    );

    // Pause when off-screen
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              startTimer();
            } else {
              stopTimer();
            }
          });
        },
        { threshold: 0.25 }
      );
      observer.observe(root);
    }

    // Pause when tab hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopTimer();
      } else {
        startTimer();
      }
    });

    setActive(0);
    startTimer();
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify theme editor section load
  document.addEventListener('shopify:section:load', (event) => {
    if (!event || !event.target) return;
    event.target.querySelectorAll(SELECTOR).forEach(initSlider);
  });
})();
